import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { AppButton, AppEmptyState, AppPillSwitch } from '../../components/ui';
import { formatMinutes, formatWeekLabel } from '../../utils/tempsWeek';

const PERSON_COLORS = ['#2f6fb5', '#a4552c', '#2e7d6b', '#5f6a2a', '#b0463f', '#4d5fa0', '#8a6420', '#3f6f8f'];

const CHARGE_COLORS = {
  late: '#e0962f',
  validee: '#3a9a62',
  a_faire: '#c9cdd4',
};

const STATUS_META = {
  a_faire: { label: 'À faire', bg: '#f1f2f4', fg: '#4a4f5c' },
  validee: { label: 'Validée', bg: '#e3f4ea', fg: '#1f7a45' },
  archivee: { label: 'Archivée', bg: '#f1f2f4', fg: '#6b7280' },
};

const SORT_OPTS = [
  { value: 'charge', label: 'Charge' },
  { value: 'avancement', label: 'Avancement' },
  { value: 'retards', label: 'Retards' },
  { value: 'nom', label: 'Nom' },
];

const initials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const colorForUser = (id) => {
  const n = Number(id) || 0;
  return PERSON_COLORS[Math.abs(n) % PERSON_COLORS.length];
};

const plural = (n, word) => `${n} ${word}${n > 1 ? 's' : ''}`;

const sumEstimated = (tasks = []) => tasks.reduce((s, t) => s + (Number(t.estimatedMinutes) || 0), 0);

const sumActual = (tasks = []) => tasks.reduce((s, t) => s + (Number(t.actualMinutes) || 0), 0);

const avgVariancePct = (tasks = []) => {
  const done = tasks.filter((t) => t.status === 'validee' && Number(t.estimatedMinutes) > 0 && t.actualMinutes != null);
  if (done.length === 0) return null;
  const avg = done.reduce((s, t) => s + (t.actualMinutes - t.estimatedMinutes) / t.estimatedMinutes, 0) / done.length;
  return Math.round(avg * 100);
};

const formatEcart = (prevu, reel, done) => {
  if (!done && !(reel > prevu)) return '—';
  const d = reel - prevu;
  if (d === 0) return formatMinutes(0);
  return `${d > 0 ? '+' : '−'}${formatMinutes(Math.abs(d))}`;
};

const buildMemberCard = (member) => {
  const weekTasks = member.tasks || [];
  const overdueTasks = member.overdueTasks || [];
  const all = [...overdueTasks, ...weekTasks];
  const planned = sumEstimated(all);
  const actual = sumActual(all);
  const pct = planned > 0 ? Math.round((actual / planned) * 100) : 0;
  const done = weekTasks.filter((t) => t.status === 'validee').length;
  const todo = weekTasks.filter((t) => t.status === 'a_faire').length;
  const lateMins = sumEstimated(overdueTasks);
  const doneMins = sumEstimated(weekTasks.filter((t) => t.status === 'validee'));
  const todoMins = sumEstimated(weekTasks.filter((t) => t.status === 'a_faire'));
  const segs = [
    { key: 'late', mins: lateMins, bg: CHARGE_COLORS.late, title: `${formatMinutes(lateMins)} en retard` },
    { key: 'validee', mins: doneMins, bg: CHARGE_COLORS.validee, title: `${formatMinutes(doneMins)} validée` },
    { key: 'a_faire', mins: todoMins, bg: CHARGE_COLORS.a_faire, title: `${formatMinutes(todoMins)} à faire` },
  ].filter((s) => s.mins > 0);
  const ecPct = avgVariancePct(weekTasks);

  return {
    id: member.user?.id,
    name: member.user?.name || '—',
    color: colorForUser(member.user?.id),
    ini: initials(member.user?.name),
    planned,
    actual,
    pct,
    todo,
    done,
    late: overdueTasks.length,
    empty: all.length === 0,
    segs,
    ecPct,
    weekTasks,
    overdueTasks,
    allCount: all.length,
  };
};

const TaskRow = ({ task, late = false }) => {
  const meta = STATUS_META[task.status] || STATUS_META.a_faire;
  const prevu = Number(task.estimatedMinutes) || 0;
  const reel = Number(task.actualMinutes) || 0;
  const done = task.status === 'validee';
  const ecart = formatEcart(prevu, reel, done);
  const over = reel > prevu && (done || reel > prevu);

  return (
    <div className={`tp-equipe__row${late ? ' is-late' : ''}`}>
      <div className="tp-equipe__row-task">
        <span className="tp-equipe__row-title">{task.title}</span>
        <span className="tp-equipe__row-meta">
          {task.productLabel || task.productRef || 'Tâche'}
          {late && task.weekStart ? ` · Prévue sem. ${formatWeekLabel(task.weekStart)}` : ''}
        </span>
      </div>
      <span className="tp-equipe__row-cmd">#{task.commandeId || '—'}</span>
      <span>
        <span className="tp-equipe__badge" style={{ background: meta.bg, color: meta.fg }}>{meta.label}</span>
      </span>
      <span className="tp-equipe__row-num">{formatMinutes(prevu)}</span>
      <span className="tp-equipe__row-num">{reel ? formatMinutes(reel) : '—'}</span>
      <span className={`tp-equipe__row-num${over ? ' is-over' : ''}`}>{ecart}</span>
    </div>
  );
};

const TempsEquipePanel = ({
  teamStats,
  loading = false,
  onGoPlanningUnassigned,
}) => {
  const [sort, setSort] = useState('charge');
  const [expanded, setExpanded] = useState({});

  const members = teamStats?.members || [];
  const hasSplitCounts = teamStats?.unassignedWeekTodoCount != null;
  const unasWeek = Number(
    hasSplitCounts ? teamStats.unassignedWeekTodoCount : (teamStats?.unassignedTodoCount ?? 0),
  );
  const unasNoWeek = Number(hasSplitCounts ? (teamStats?.unscheduledTodoCount ?? 0) : 0);
  const unasTotal = Number(teamStats?.unassignedTodoCount ?? unasWeek + unasNoWeek);

  const cards = useMemo(() => {
    const raw = members.map(buildMemberCard);
    const sorters = {
      charge: (a, b) => b.planned - a.planned,
      avancement: (a, b) => b.pct - a.pct,
      retards: (a, b) => b.late - a.late || b.planned - a.planned,
      nom: (a, b) => String(a.name).localeCompare(String(b.name), 'fr'),
    };
    return [...raw].sort(
      (a, b) => (b.empty ? 0 : 1) - (a.empty ? 0 : 1) || sorters[sort](a, b),
    );
  }, [members, sort]);

  const maxCharge = Math.max(60, ...cards.map((c) => c.planned), 0);
  const totalPlanned = cards.reduce((s, c) => s + c.planned, 0);
  const totalActual = cards.reduce((s, c) => s + c.actual, 0);
  const teamPct = totalPlanned > 0 ? Math.round((totalActual / totalPlanned) * 100) : 0;
  const totalLate = cards.reduce((s, c) => s + c.late, 0);
  const allOpen = cards.length > 0 && cards.every((c) => expanded[c.id]);

  const toggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAll = () => {
    const next = {};
    cards.forEach((c) => {
      next[c.id] = !allOpen;
    });
    setExpanded(next);
  };

  if (loading) {
    return (
      <div className="tp-equipe" aria-busy="true">
        <div className="tp-equipe__grid">
          {[1, 2, 3].map((k) => (
            <div key={k} className="tp-equipe__skeleton" />
          ))}
        </div>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="tp-equipe">
        <AppEmptyState message="Aucune personne avec le rôle Production." />
      </div>
    );
  }

  return (
    <div className="tp-equipe">
      <div className="tp-equipe__summary">
        <div className="tp-equipe__summary-main">
          <span className="tp-equipe__summary-label">Avancement équipe</span>
          <div className="tp-equipe__summary-metric">
            <strong>{teamPct} %</strong>
            <span>{formatMinutes(totalActual)} validé sur {formatMinutes(totalPlanned)} prévu</span>
          </div>
          <div className="tp-equipe__bar">
            <div className="tp-equipe__bar-fill tp-equipe__bar-fill--ink" style={{ width: `${Math.min(100, teamPct)}%` }} />
          </div>
        </div>
        <div className="tp-equipe__summary-late">
          <span className="tp-equipe__summary-label">En retard</span>
          <strong className={totalLate > 0 ? 'is-warn' : ''}>{totalLate}</strong>
        </div>
        <div className="tp-equipe__summary-unas">
          <span className={`tp-equipe__unas-icon${unasTotal > 0 ? ' is-warn' : ' is-ok'}`}>
            <Icon icon={unasTotal > 0 ? 'mdi:account-outline' : 'mdi:check-circle-outline'} width={18} />
          </span>
          <div className="tp-equipe__unas-text">
            <strong>
              {unasWeek > 0
                ? `${plural(unasWeek, 'tâche')} non assignée${unasWeek > 1 ? 's' : ''} cette semaine`
                : 'Toutes les tâches de la semaine sont assignées'}
            </strong>
            {unasNoWeek > 0 && (
              <span>+ {plural(unasNoWeek, 'tâche')} sans semaine</span>
            )}
          </div>
          {unasTotal > 0 && (
            <AppButton variant="outline-secondary" size="sm" onClick={onGoPlanningUnassigned}>
              Répartir dans Planning
              <Icon icon="mdi:arrow-right" width={14} className="ms-1" aria-hidden />
            </AppButton>
          )}
        </div>
      </div>

      <div className="tp-equipe__toolbar">
        <div className="tp-equipe__legend">
          <span className="tp-equipe__legend-title">Charge prévue</span>
          <span className="tp-equipe__legend-item"><span style={{ background: CHARGE_COLORS.late }} />En retard</span>
          <span className="tp-equipe__legend-item"><span style={{ background: CHARGE_COLORS.validee }} />Validée</span>
          <span className="tp-equipe__legend-item"><span style={{ background: CHARGE_COLORS.a_faire }} />À faire</span>
          <span className="tp-equipe__legend-scale">· Échelle commune · max {formatMinutes(maxCharge)}</span>
        </div>
        <div className="tp-equipe__toolbar-right">
          <span className="tp-equipe__sort-label">Trier par</span>
          <AppPillSwitch variant="segment" options={SORT_OPTS} value={sort} onChange={setSort} ariaLabel="Trier par" />
          <button type="button" className="tp-equipe__toggle-all" onClick={toggleAll}>
            <Icon icon="mdi:unfold-more-horizontal" width={16} />
            {allOpen ? 'Tout replier' : 'Tout déplier'}
          </button>
        </div>
      </div>

      <div className="tp-equipe__grid">
        {cards.map((card) => {
          const open = Boolean(expanded[card.id]);
          const chargeW = maxCharge > 0 ? (card.planned / maxCharge) * 100 : 0;
          const ecLabel = card.ecPct == null
            ? '—'
            : `${card.ecPct > 0 ? '+' : card.ecPct < 0 ? '−' : ''}${Math.abs(card.ecPct)} %`;

          return (
            <article
              key={card.id}
              className={`tp-equipe__card${open ? ' is-open' : ''}`}
              aria-label={card.name}
            >
              <button
                type="button"
                className="tp-equipe__card-head"
                onClick={() => toggle(card.id)}
                aria-expanded={open}
              >
                <div className="tp-equipe__card-top">
                  <span className="tp-equipe__avatar" style={{ background: card.color }}>{card.ini}</span>
                  <span className="tp-equipe__card-name">{card.name}</span>
                  <span className="tp-equipe__card-pct">{card.pct} %</span>
                </div>
                <div className="tp-equipe__card-progress">
                  <div className="tp-equipe__bar">
                    <div
                      className={`tp-equipe__bar-fill${card.pct > 100 ? ' is-over' : ' tp-equipe__bar-fill--ink'}`}
                      style={{ width: `${Math.min(100, card.pct)}%` }}
                    />
                  </div>
                  <div className="tp-equipe__card-times">
                    <span>Validé <strong>{formatMinutes(card.actual)}</strong></span>
                    <span>Prévu <strong>{formatMinutes(card.planned)}</strong></span>
                  </div>
                </div>
                <div className="tp-equipe__charge">
                  <span className="tp-equipe__summary-label">Charge prévue</span>
                  <div className="tp-equipe__charge-track">
                    <div className="tp-equipe__charge-inner" style={{ width: `${chargeW}%` }}>
                      {card.segs.map((g) => (
                        <div
                          key={g.key}
                          title={g.title}
                          style={{ width: `${(g.mins / (card.planned || 1)) * 100}%`, background: g.bg }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                {card.empty ? (
                  <div className="tp-equipe__card-empty">Aucune tâche cette semaine</div>
                ) : (
                  <div className="tp-equipe__stats">
                    <div><strong>{card.todo}</strong><span>À faire</span></div>
                    <div><strong>{card.done}</strong><span>Validées</span></div>
                    <div>
                      <strong className={card.late > 0 ? 'is-warn' : ''}>{card.late}</strong>
                      <span className={card.late > 0 ? 'is-warn' : ''}>En retard</span>
                    </div>
                    <div title={card.ecPct == null ? 'Aucune tâche validée cette semaine' : `Écart moyen réel vs prévu sur ${plural(card.done, 'tâche validée')}`}>
                      <strong className={card.ecPct != null && card.ecPct > 10 ? 'is-warn' : ''}>{ecLabel}</strong>
                      <span>Écart moy.</span>
                    </div>
                  </div>
                )}
                {!card.empty && (
                  <div className="tp-equipe__card-toggle">
                    {open ? 'Masquer le détail' : `Voir le détail · ${plural(card.allCount, 'tâche')}`}
                    <Icon
                      icon="mdi:chevron-down"
                      width={16}
                      className={`tp-equipe__chevron${open ? ' is-open' : ''}`}
                    />
                  </div>
                )}
              </button>

              {open && !card.empty && (
                <div className="tp-equipe__detail">
                  <div className="tp-equipe__detail-cols" aria-hidden="true">
                    <span>Tâche</span>
                    <span>Commande</span>
                    <span>Statut</span>
                    <span className="tp-equipe__row-num">Prévu</span>
                    <span className="tp-equipe__row-num">Réel</span>
                    <span className="tp-equipe__row-num">Écart</span>
                  </div>
                  {card.overdueTasks.length > 0 && (
                    <>
                      <div className="tp-equipe__detail-section is-late">
                        <Icon icon="mdi:clock-alert-outline" width={16} />
                        En retard
                        <span>· {plural(card.overdueTasks.length, 'tâche')}</span>
                      </div>
                      {card.overdueTasks.map((t) => (
                        <TaskRow key={t.id} task={t} late />
                      ))}
                    </>
                  )}
                  <div className="tp-equipe__detail-section">
                    <Icon icon="mdi:calendar-check" width={16} />
                    Cette semaine
                    <span>· {plural(card.weekTasks.length, 'tâche')}</span>
                  </div>
                  {card.weekTasks.length === 0 ? (
                    <div className="tp-equipe__detail-empty">Rien de planifié sur la semaine.</div>
                  ) : (
                    card.weekTasks.map((t) => <TaskRow key={t.id} task={t} />)
                  )}
                  <div className="tp-equipe__detail-note">
                    <Icon icon="mdi:lock-outline" width={12} />
                    Lecture seule · l’assignation se fait dans Planning
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default TempsEquipePanel;
